import { useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { SelectInput, TokenSelector } from '~/components/component-library'
import { EarnFormValues } from '~/components/forms/earn/earnSchema'
import { useActiveOrg } from '~/hooks/org'
import { useSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { useVaultAssets } from '~/hooks/useTokens'
import { Address } from '~/utils/address'
import { getAutoEarnModule } from '~/utils/vault'

const PAUSE_AUTO_EARN_OPTIONS = [
  { value: 300, label: '5 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 86400, label: '1 day' },
]

export const EarnWithdrawSection = ({
  onInsufficientBalanceChange,
}: {
  onInsufficientBalanceChange: (insufficient: boolean) => void
}) => {
  const { control } = useFormContext<EarnFormValues>()

  const account = useWatch({ control, name: 'account' })
  const chainId = useWatch({ control, name: 'vault.chainId' })
  const vaultToken = useWatch({ control, name: 'vault.token' })
  const assetToken = useWatch({ control, name: 'asset.token' })
  const { smartAccounts } = useSmartAccounts()
  const { featureFlags } = useActiveOrg()

  const senderAccount = smartAccounts.find(
    (smartAccount) => smartAccount.address === account,
  )

  const { balances } = useSmartAccountBalances(senderAccount?.address, {
    chainId,
  })

  const currentBalance =
    balances.find(
      (balance) =>
        balance.chainId === chainId &&
        Address.equal(balance.address, vaultToken),
    )?.amount ?? 0n

  const { data: vaultAssets } = useVaultAssets({
    vault: vaultToken,
    chainId,
    balance: currentBalance,
  })

  const { convertTokenToUSD } = useTokenPrices()
  const overrideAmount = vaultAssets ?? 0n

  const balanceOverride = useMemo(
    () => ({
      amount: overrideAmount,
      totalUsdValue:
        convertTokenToUSD({
          token: assetToken,
          amount: overrideAmount,
          chainId,
        }) ?? 0,
    }),
    [overrideAmount, convertTokenToUSD, assetToken, chainId],
  )

  return (
    <>
      <TokenSelector<EarnFormValues>
        control={control}
        label="Withdraw amount"
        addressInputName="asset.token"
        chainIdInputName="asset.chainId"
        amountInputName="asset.amount"
        denomination="token"
        showBalances
        sortBy="value"
        disabled
        balanceOverride={balanceOverride}
        onExceedsBalanceChange={onInsufficientBalanceChange}
      />
      {featureFlags?.['workspace-auto-earn'] &&
        senderAccount &&
        getAutoEarnModule(senderAccount, chainId) && (
          <SelectInput<number, EarnFormValues>
            control={control}
            inputName="pauseAutoEarnSeconds"
            label="Pause auto-earn"
            tooltip="Choose how long to pause auto-earn on this account so that your funds are not immediately redeposited."
            placeholder="5 minutes"
            options={PAUSE_AUTO_EARN_OPTIONS}
          />
        )}
    </>
  )
}
